import { CallNote, DealBasics, Deck } from './types';

// Calls the generateMemo Cloud Function directly rather than through a Hosting
// rewrite, because Hosting cuts proxied requests off at 60 seconds.
const MEMO_API_URL =
  process.env.REACT_APP_MEMO_API_URL ?? 'https://us-central1-theapplebythetree.cloudfunctions.net/generateMemo';

export interface MemoUsage {
  input_tokens: number;
  output_tokens: number;
}

export type MemoEvent =
  | { type: 'text'; text: string }
  | { type: 'done'; model: string; usage: MemoUsage }
  | { type: 'error'; error: string };

export interface MemoInputs {
  deal: DealBasics;
  deck: Deck | null;
  notes: CallNote[];
  thesis: string;
}

export async function streamMemo(
  inputs: MemoInputs,
  onEvent: (event: MemoEvent) => void,
  signal: AbortSignal,
): Promise<void> {
  const res = await fetch(MEMO_API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      deal: inputs.deal,
      deck: inputs.deck && {
        fileName: inputs.deck.fileName,
        slides: inputs.deck.slides.map((s) => ({ text: s.text, imageDataUrl: s.imageDataUrl })),
      },
      notes: inputs.notes.map(({ label, date, text }) => ({ label, date, text })),
      thesis: inputs.thesis,
    }),
    signal,
  });

  if (!res.ok || !res.body) {
    const body = await res.json().catch(() => null);
    throw new Error(body?.error ?? `The memo service returned an error (${res.status}).`);
  }

  // The function streams newline-delimited JSON events.
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let newline;
    while ((newline = buffer.indexOf('\n')) >= 0) {
      const line = buffer.slice(0, newline).trim();
      buffer = buffer.slice(newline + 1);
      if (line) onEvent(JSON.parse(line) as MemoEvent);
    }
  }
}
